import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Download, Loader2, AlertCircle } from 'lucide-react';
import { Button } from './button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const PdfViewer = ({ policyId, title }) => {
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imgSrc, setImgSrc] = useState('');

  const token = localStorage.getItem('token');

  // Fetch page count
  useEffect(() => {
    if (!policyId) return;
    fetch(`${BACKEND_URL}/api/policies/${policyId}/info`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => {
        if (data.total_pages > 0) {
          setTotalPages(data.total_pages);
        } else {
          setError('This policy file needs to be re-uploaded by an admin.');
        }
      })
      .catch(() => setError('Could not load document info.'));
  }, [policyId, token]);

  // Load page image
  useEffect(() => {
    if (!policyId || totalPages === 0) return;
    setLoading(true);
    setError(null);

    fetch(`${BACKEND_URL}/api/policies/${policyId}/preview?page=${currentPage}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => {
        if (!r.ok) throw new Error('Failed to load page');
        return r.blob();
      })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        setImgSrc(prev => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load this page.');
        setLoading(false);
      });
  }, [policyId, currentPage, totalPages, token]);

  const handleDownload = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/policies/${policyId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${title || 'policy'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      setError('Download failed. Please try again.');
    }
  };

  return (
    <div className="flex flex-col" data-testid="pdf-viewer">
      {/* Image display */}
      <div className="overflow-auto bg-slate-100 rounded-lg flex justify-center items-start" style={{ maxHeight: '62vh', minHeight: '200px' }}>
        {loading && !error ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-[#1D35A0] animate-spin" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center gap-2 py-12 text-center px-4">
            <AlertCircle className="w-8 h-8 text-slate-400" />
            <p className="text-slate-500 text-sm">{error}</p>
          </div>
        ) : (
          <img
            src={imgSrc}
            alt={`${title} - Page ${currentPage}`}
            className="max-w-full h-auto"
            data-testid="pdf-page-image"
          />
        )}
      </div>

      {/* Controls - always show */}
      <div className="flex items-center justify-between pt-3 border-t mt-3">
        {totalPages > 1 ? (
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1 || loading} className="h-8 w-8 p-0" data-testid="pdf-prev-page">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-xs text-slate-600 px-2">{currentPage} / {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages || loading} className="h-8 w-8 p-0" data-testid="pdf-next-page">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        ) : <div />}

        <Button size="sm" onClick={handleDownload} className="bg-[#1D35A0] hover:bg-[#152A80] text-white h-8" data-testid="pdf-download-btn">
          <Download className="w-3.5 h-3.5 mr-1.5" /> Download
        </Button>
      </div>
    </div>
  );
};
